import * as Yup from 'yup';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Form, Formik, FormikProps } from 'formik';
import { FormikInputText } from 'components/forms/formik/FormikInputText';
import { usePanel } from 'hooks/usePanel';
import { BaseSection } from 'components/layout/Section/BaseSection';
import { useQuoteContractualizationData, useIsSendingContract } from '../../store';
import Loading from 'components/ui/Loader';
import { quotesAPI } from 'services/swagger-api';
import { MandateSignatoryDto } from 'api/quote';
import { useTranslation } from 'react-i18next';
import { NsKey } from 'enums/NsKey';
import { DisplayTypeEnum } from 'api/files';
import { GenericColors } from 'api/tenant';
import { toasterService } from 'components/feedback/Toaster/toasterService';
import { parseApiError } from 'shared/utils/apiValidationErrors';
import { frenchMobileRule, normalizeFrenchMobile } from 'shared/utils/frenchMobile';

interface MandateSignatureFormValues {
  email: string;
  phone: string;
}

interface MandateSignatureRequestProps {
  onSuccess?: () => void;
}

export const MandateSignatureRequest: React.FC<MandateSignatureRequestProps> = ({
  onSuccess
}) => {
  const formikRef = useRef<FormikProps<MandateSignatureFormValues>>(null);

  const quoteData = useQuoteContractualizationData();
  const isSendingContract = useIsSendingContract();
  const { t } = useTranslation(NsKey.Mandate);

  const { closePanel, setPanelActions } = usePanel();

  const [isFormValid, setIsFormValid] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Mandate signatory: the debtor when a natural person, otherwise the legal entity's
  // manager. Their mobile receives the signature code: no mobile, no sending.
  const [signatory, setSignatory] = useState<MandateSignatoryDto | null | undefined>(undefined);
  const quoteId = quoteData?.id;
  useEffect(() => {
    if (!quoteId) return;
    let cancelled = false;
    quotesAPI
      .getMandateSignatory({ quoteId })
      .then((result) => { if (!cancelled) setSignatory(result ?? null); })
      .catch(() => { if (!cancelled) setSignatory(null); });
    return () => { cancelled = true; };
  }, [quoteId]);
  const canSign = signatory?.canSign === true;

  // The signatory's mobile receives the signature code: the broker checks it here before
  // sending — prefilled from the record, editable, French mobile format. The back stores
  // the confirmed value on the signatory's record.
  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        email: Yup.string().email('Email invalide').required("L'email est requis"),
        phone: frenchMobileRule(t('SIGNATURE_REQUEST.PHONE_INVALID'), t('SIGNATURE_REQUEST.PHONE_REQUIRED'))
      }),
    [t]
  );

  useEffect(() => {
    if (formikRef.current) {
      setPanelActions({
        closeLabel: 'COMMON.CANCEL',
        submitLabel: 'COMMON.VALIDATE',
        submitDisabled: !isFormValid || isSendingContract || isSubmitting || !canSign,
        onSubmit: () => {
          formikRef.current?.submitForm();
        }
      });
    }
  }, [formikRef, setPanelActions, isFormValid, isSendingContract, isSubmitting, canSign]);

  if (!quoteData) {
    return <Loading isFullPage loading={true} />;
  }

  const { insured } = quoteData;

  const handleSubmit = async (values: MandateSignatureFormValues) => {
    if (isSubmitting) return;
    try {
      if (!quoteData.mandateId) return;
      setIsSubmitting(true);
      await quotesAPI.createMandateSignature({
        quoteId: quoteData.id,
        createMandateSignatureCommand: {
          mandateId: quoteData.mandateId,
          toAddress: values.email,
          signatoryPhone: normalizeFrenchMobile(values.phone)
        }
      });

      onSuccess?.();

      closePanel();
    } catch (e) {
      console.error(e);
      const parsed = await parseApiError(e);
      toasterService.showToast({
        message: parsed.message || t('SIGNATURE_REQUEST.SEND_FAILED'),
        displayType: DisplayTypeEnum.Toaster,
        color: GenericColors.Red,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Formik<MandateSignatureFormValues>
      enableReinitialize
      // The link goes to the signatory: the firm's manager when the firm pays, the
      // syndic's manager otherwise. The insured is the right recipient only when they
      // sign themselves.
      initialValues={{
        email: signatory?.email || insured.email || '',
        phone: signatory?.phone ?? ''
      }}
      validationSchema={validationSchema}
      innerRef={formikRef}
      onSubmit={handleSubmit}
    >
      {({ isValid }) => {
        if (isValid !== isFormValid) {
          setIsFormValid(isValid);
        }
        return (
          <Form>
            <BaseSection title={t('SIGNATURE_REQUEST.SIGNATORY_TITLE')}>
              <div className="col-span-6 mt-2 text-sm">
                {signatory === undefined ? (
                  <p className="text-gray-500">{t('SIGNATURE_REQUEST.SEARCHING')}</p>
                ) : signatory === null ? (
                  <p className="text-red-600">{t('SIGNATURE_REQUEST.NO_SIGNATORY')}</p>
                ) : (
                  <>
                    <p className="font-medium text-gray-800">
                      {signatory.fullName}
                      {signatory.isDebtor
                        ? ''
                        : t('SIGNATURE_REQUEST.DEBTOR_REPRESENTATIVE', { debtor: signatory.debtorName ?? '' })}
                    </p>
                    <p className="text-gray-600">
                      {signatory.email || t('SIGNATURE_REQUEST.EMAIL_MISSING')}
                    </p>
                    {signatory.isCurrentUser && (
                      <p className="mt-1 text-gray-600">
                        {t('SIGNATURE_REQUEST.YOU_SIGN', { debtor: signatory.debtorName ?? '' })}
                      </p>
                    )}
                    {!signatory.canSign && (
                      <p className="mt-1 text-red-600">{t('SIGNATURE_REQUEST.CONTACT_REQUIRED')}</p>
                    )}
                  </>
                )}
              </div>
              {signatory && (
                <div className="col-span-6 mt-2">
                  <FormikInputText
                    label={t('SIGNATURE_REQUEST.PHONE_LABEL')}
                    required
                    type="tel"
                    name="phone"
                    placeholder={t('SIGNATURE_REQUEST.PHONE_PLACEHOLDER')}
                    disabled={isSendingContract || isSubmitting}
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    {signatory.phone
                      ? t('SIGNATURE_REQUEST.PHONE_CHECK_HINT')
                      : t('SIGNATURE_REQUEST.PHONE_HINT')}
                  </p>
                </div>
              )}
            </BaseSection>
            <BaseSection title={t('SIGNATURE_REQUEST.RECIPIENT_TITLE')}>
              <>
                <div className="col-span-6 mt-2">
                  <FormikInputText
                    label={t('SIGNATURE_REQUEST.EMAIL_LABEL')}
                    required
                    type="email"
                    name="email"
                    placeholder={t('SIGNATURE_REQUEST.EMAIL_PLACEHOLDER')}
                    // Locked until the signatory is known: enableReinitialize would
                    // otherwise wipe what the broker typed when the lookup resolves.
                    disabled={signatory === undefined || isSendingContract || isSubmitting}
                  />
                </div>
              </>
            </BaseSection>
          </Form>
        );
      }}
    </Formik>
  );
};
